import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComplianceTimeSeriesFilter, ComplianceTimeSeriesPoint, ComplianceTimeSeriesRequest } from '../../../models/dashboard.model';
import { ComplianceFiltersComponent } from '../../../../compliance/_shared/components/compliance-filters/compliance-filters.component';
import { ComplianceStateService } from '../../../../compliance/_shared/services/compliance-state.service';
import { ComplianceOverviewRequirementsRequest } from '../../../../compliance/compliance-overview/models/compliance-item.model';
import {
  MultiLineChartComponent,
  MultiLineChartDataPoint,
  MultiLineChartSeries,
  LegendItem
} from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { ComplianceService } from '../../../../compliance/_shared/services/compliance.service';
import { ComplianceKpiRequest } from '../../../../compliance/_shared/models/compliance-kpi.model';
import { buildComplianceChart, getComplianceGrouping } from '../../../helpers/compliance-chart.helper';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    MultiLineChartComponent,
    EmptyStateComponent,
    GraphTrendIconComponent,
    ComplianceFiltersComponent,
    DateFilterComponent
  ],
  providers: [FilterService, ComplianceStateService],
  templateUrl: './compliance-modal.component.html',
  styleUrl: './compliance-modal.component.scss'
})
export class ComplianceModalComponent implements OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private dashboardFilterService = inject(FilterService, { skipSelf: true });
  private complianceStateService = inject(ComplianceStateService);
  private dashboardService = inject(DashboardService);
  private complianceService = inject(ComplianceService);
  private errorHandlerService = inject(ErrorHandlerService);

  chartData: MultiLineChartDataPoint[] = [];
  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly chartHeight = 335;

  readonly yAxisFormatter = (value: any): string => {
    return `${Number(value).toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  isLoading: boolean = true;
  title: string = 'maintenanceDashboard.charts.compliance';
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  private modalFilter: WidgetFilterData;
  private complianceFilter: ComplianceTimeSeriesFilter = {};
  private points: ComplianceTimeSeriesPoint[] = [];

  constructor() {
    const dashboardFilter = this.dashboardFilterService.filter;

    this.modalFilter = { ...dashboardFilter, accountIds: [...(dashboardFilter.accountIds ?? [])] };
    this.accountFilter = { accountIds: [...this.modalFilter.accountIds] };
    this.dateFilter = {} as DateIntervalFilterData;

    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChart());

    this.complianceStateService.updateComplianceRequest({ accountIds: [...this.modalFilter.accountIds], page: 1, pageSize: 10 });
    this.complianceStateService.request$
      .pipe(takeUntilDestroyed())
      .subscribe(request => this.onComplianceFilterChange(request));
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async onComplianceFilterChange(request: ComplianceOverviewRequirementsRequest): Promise<void> {
    const region = request.complianceRegion;

    this.accountFilter = { accountIds: [...(request.accountIds ?? [])] };
    this.complianceFilter = {
      regionId: region?.type === 'region' ? region.id : undefined,
      countryId: region?.type === 'country' ? region.id : undefined,
      complianceId: request.complianceId ?? undefined,
      assetTypeId: request.assetTypeId ?? undefined
    };

    await this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    await this.updateFilter();
  }

  private async updateFilter(): Promise<void> {
    if (isEmptyObject(this.dateFilter) || isEmptyObject(this.accountFilter)) return;

    this.modalFilter = {
      ...this.modalFilter,
      ...this.dateFilter,
      accountIds: [...(this.accountFilter.accountIds ?? [])]
    };

    await this.loadComplianceData();
  }

  async loadComplianceData(): Promise<void> {
    this.isLoading = true;

    try {
      const filter = this.modalFilter;

      const request: ComplianceTimeSeriesRequest = {
        accountIds: filter.accountIds,
        timeFrom: filter.startDate!,
        timeTo: filter.endDate!,
        interval: filter.interval,
        ...this.complianceFilter
      };

      const statsRequest: ComplianceKpiRequest = {
        accountIds: filter.accountIds,
        timeFrom: filter.startDate!,
        timeTo: filter.endDate!,
        previousTimeFrom: filter.previousCycleStartDate!,
        previousTimeTo: filter.previousCycleEndDate!,
        ...this.complianceFilter
      };

      this.cancelSubject.next();

      const [response, statsResponse] = await Promise.all([
        this.dashboardService.getAssetComplianceTimeSeries(request, this.cancelSubject),
        this.complianceService.getCompliantKpiStatsAsync(statsRequest, this.cancelSubject)
      ]);

      this.points = response.data ?? [];
      this.buildChart();

      this.totalValue = statsResponse?.data?.totalValue ?? 0;
      this.percentageChange = statsResponse?.data?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.points = [];
          this.buildChart();
          this.totalValue = 0;
          this.percentageChange = 0;
        }
      });
    }
  }

  private buildChart(): void {
    const { startDate, endDate } = this.modalFilter;
    if (!startDate || !endDate) return;

    const chart = buildComplianceChart(
      this.points,
      startDate,
      endDate,
      getComplianceGrouping(startDate, endDate, 31),
      (key) => this.transloco.translate(key),
      true
    );

    this.chartData = chart.data;
    this.series = chart.series;
    this.legendItems = chart.legend;
    this.calculateYTicks(this.chartData);
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues: number[] = [];

    data.forEach(point => {
      this.series.forEach(s => {
        const value = point[s.key];
        if (typeof value === 'number') {
          allValues.push(value);
        }
      });
    });

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatValue(): string {
    return this.transloco.translate('maintenanceDashboard.charts.compliantAssets', { count: this.totalValue.toLocaleString('default') });
  }

  formatTooltipValue = (value: number): string => {
    return `${value.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  hasKpi(): boolean {
    return this.hasData() || this.totalValue !== 0 || this.percentageChange !== 0;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}